import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  ArcElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PaymentIcon from "@mui/icons-material/Payment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import HistoryIcon from "@mui/icons-material/History";
import styles from "./dashboard.module.css";
import { useAuthStore } from "../../store/auth-store";
import { apiClient } from "../../services/api-client";
import { canAccessPath, type UserRole } from "../../utils/permissions";
import dayjs from "dayjs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface StatusBreakdown {
  status: string;
  count: number;
}

interface EnrollmentSummary {
  programId: string;
  programName: string;
  count: number;
}

interface FinanceActivityDto {
  id: string;
  type: string;
  description?: string | null;
  createdAt: string;
  actor?: { id: string; email: string; role: string } | null;
}

interface DashboardStats {
  activeCases: number;
  pendingApplications: number;
  tasksDueToday: number;
  caseStatusBreakdown: StatusBreakdown[];
  applicationStatusBreakdown: StatusBreakdown[];
  programEnrollmentTop: EnrollmentSummary[];
  paysheetStatusBreakdown: StatusBreakdown[];
  paysheetAuthorizedTotal: number;
  paysheetPendingApproval: number;
  recentFinanceActivities: FinanceActivityDto[];
}

interface TransactionRow {
  id: string;
  name: string;
  account: string;
  timestamp: string;
  amount: number;
  status: "completed" | "pending" | "declined";
}

interface SavingPlan {
  id: string;
  name: string;
  current: number;
  goal: number;
}

interface ActivityLog {
  id: string;
  icon: React.ReactNode;
  text: string;
  time: string;
}

const NAV_ITEMS = [
  { label: "Dashboard", icon: <DashboardIcon fontSize="small" />, path: "/" },
  { label: "Cases", icon: <PeopleAltIcon fontSize="small" />, path: "/cases" },
  { label: "Applications", icon: <DescriptionIcon fontSize="small" />, path: "/applications" },
  { label: "Paysheets", icon: <PaymentIcon fontSize="small" />, path: "/paysheets" },
  { label: "Tasks", icon: <AssignmentTurnedInIcon fontSize="small" />, path: "/tasks" },
  { label: "Programs", icon: <AssessmentIcon fontSize="small" />, path: "/programs" },
  { label: "Participants", icon: <TimelineIcon fontSize="small" />, path: "/participants" },
  { label: "Users", icon: <ManageAccountsIcon fontSize="small" />, path: "/users" },
  { label: "Audit Logs", icon: <HistoryIcon fontSize="small" />, path: "/audit-logs" }
];

const statusOrder = ["PENDING", "IN_PROGRESS", "APPROVED", "REJECTED", "COMPLETED"];

const toTitleCase = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const mapStatusCounts = (breakdown: StatusBreakdown[]) => {
  const map = new Map<string, number>();
  breakdown.forEach((item) => {
    map.set(item.status.toUpperCase(), item.count);
  });
  return statusOrder.map((status) => map.get(status) ?? 0);
};

const legendColors = ["#0d6efd", "#6610f2", "#20c997", "#fd7e14", "#6f42c1"];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);

interface SearchResult {
  type: 'participant' | 'case' | 'program';
  id: string;
  title: string;
  subtitle?: string;
  path: string;
}

export const Dashboard = () => {
  const session = useAuthStore((state) => state.session);
  const signOut = useAuthStore((state) => state.signOut);
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await apiClient.get<{ status: string; data: DashboardStats }>(
          "/dashboard/stats"
        );
        setStats(data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unable to load dashboard data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats().catch(() => {
      setError("Unable to load dashboard data");
      setIsLoading(false);
    });
  }, []);

  // Debounced search effect
  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const { data } = await apiClient.get<{ status: string; data: { results: SearchResult[] } }>(
          `/search?q=${encodeURIComponent(searchQuery)}&limit=8`
        );
        setSearchResults(data.data.results);
        setShowSearchResults(true);
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search by 300ms
    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (result: SearchResult) => {
    navigate(result.path);
    setSearchQuery("");
    setShowSearchResults(false);
  };

  const handleSearchBlur = () => {
    // Delay hiding results to allow click events to fire
    setTimeout(() => setShowSearchResults(false), 200);
  };

  const metrics = useMemo(() => {
    const topProgram = stats?.programEnrollmentTop?.[0];
    return [
      {
        key: "activeCases",
        title: "Active Cases",
        value: stats?.activeCases ?? 0,
        trend: "+12 vs last week",
        trendClass: styles.trendPositive
      },
      {
        key: "pendingApplications",
        title: "Pending Applications",
        value: stats?.pendingApplications ?? 0,
        trend: "Review queue updated daily",
        trendClass: styles.trendNeutral
      },
      {
        key: "tasksDueToday",
        title: "Tasks Due Today",
        value: stats?.tasksDueToday ?? 0,
        trend: "Ensure follow-ups are closed",
        trendClass: styles.trendNeutral
      },
      {
        key: "topProgram",
        title: "Top Program Enrollment",
        value: topProgram ? topProgram.count : 0,
        trend: topProgram ? topProgram.programName : "Awaiting enrollment data",
        trendClass: styles.trendNeutral
      },
      {
        key: "paysheetPending",
        title: "Pending Paysheet Approvals",
        value: stats?.paysheetPendingApproval ?? 0,
        trend: "Finance approvals outstanding",
        trendClass: styles.trendNeutral
      },
      {
        key: "authorizedTotal",
        title: "Authorized Disbursements",
        value: stats ? formatCurrency(stats.paysheetAuthorizedTotal ?? 0) : formatCurrency(0),
        trend: "Across all programs",
        trendClass: styles.trendPositive
      }
    ];
  }, [stats]);

  const lineChartData = useMemo(() => {
    if (!stats) {
      return {
        labels: ["Submitted", "Processing", "Completed"],
        datasets: [
          {
            label: "Cases",
            data: [5, 8, 4],
            fill: false,
            borderColor: "#0d6efd",
            tension: 0.4,
            backgroundColor: "rgba(13, 110, 253, 0.15)",
            pointRadius: 4
          },
          {
            label: "Applications",
            data: [6, 3, 7],
            fill: false,
            borderColor: "#6610f2",
            tension: 0.4,
            backgroundColor: "rgba(102, 16, 242, 0.15)",
            pointRadius: 4
          }
        ]
      };
    }

    const caseCounts = mapStatusCounts(stats.caseStatusBreakdown);
    const applicationCounts = mapStatusCounts(stats.applicationStatusBreakdown);

    return {
      labels: statusOrder.map((status) => toTitleCase(status)),
      datasets: [
        {
          label: "Cases",
          data: caseCounts,
          fill: false,
          borderColor: "#0d6efd",
          tension: 0.4,
          backgroundColor: "rgba(13, 110, 253, 0.15)",
          pointRadius: 4
        },
        {
          label: "Applications",
          data: applicationCounts,
          fill: false,
          borderColor: "#6610f2",
          tension: 0.4,
          backgroundColor: "rgba(102, 16, 242, 0.15)",
          pointRadius: 4
        }
      ]
    };
  }, [stats]);

  const doughnutData = useMemo(() => {
    const labels = stats?.programEnrollmentTop.map((item) => item.programName) ?? [];
    const values = stats?.programEnrollmentTop.map((item) => item.count) ?? [];

    return {
      labels: labels.length ? labels : ["SEED", "BRISP", "Other"],
      datasets: [
        {
          data: values.length ? values : [45, 28, 12],
          backgroundColor: legendColors,
          borderWidth: 0
        }
      ]
    };
  }, [stats]);

  const activities = useMemo<ActivityLog[]>(() => {
    if (!stats?.recentFinanceActivities?.length) {
      return [];
    }
    return stats.recentFinanceActivities.map((activity) => ({
      id: activity.id,
      icon: <AccountBalanceIcon fontSize="small" />,
      text: activity.description ?? `Finance event: ${toTitleCase(activity.type.replace("PAYSHEET_", ""))}`,
      time: `${dayjs(activity.createdAt).format("MMM DD, YYYY h:mm A")}${
        activity.actor?.email ? ` • ${activity.actor.email}` : ""
      }`
    }));
  }, [stats]);

  if (!session) {
    return <Navigate to="/auth/login" replace />;
  }

  return (
    <div className={styles.dashboardRoot} data-testid="seed-dashboard">
      <aside className={styles.sidebar} aria-label="Primary navigation">
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarLogo}>IMS</span>
          <span className={styles.sidebarTitle}>SEED Information Management System</span>
        </div>
        <nav className={styles.nav}>
          {NAV_ITEMS.filter(item => canAccessPath(session?.user.role as UserRole, item.path)).map((item) => (
            <div
              key={item.label}
              role="button"
              tabIndex={0}
              onClick={() => navigate(item.path)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  navigate(item.path);
                }
              }}
              className={`${styles.navItem} ${
                location.pathname === item.path ? styles.navItemActive : ""
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.label === "Payments" ? <span className={styles.navBadge}>2</span> : null}
            </div>
          ))}
        </nav>
        <div className={styles.sidebarFooter}>
          <p>
            Modular by design. Expand beyond SEED with BRISP and new Ministry programs as needs grow.
          </p>
          <button
            type="button"
            className={styles.proButton}
            onClick={() => navigate("/program-modules")}
          >
            Explore Program Modules
          </button>
        </div>
      </aside>

      <main className={styles.main}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.headerTitle}>Administrative Dashboard</h1>
            <p className={styles.trendNeutral}>
              Monitor disbursements, approvals, and program adoption across SEED IMS.
            </p>
          </div>
          <div className={styles.headerActions}>
            <form onSubmit={handleSearchSubmit} className={styles.searchBar} style={{ position: "relative" }}>
              <span className={styles.searchIcon} aria-hidden="true">
                <SearchRoundedIcon fontSize="small" />
              </span>
              <input
                type="search"
                className={styles.searchInput}
                placeholder="Search participants, cases, or programs"
                aria-label="Search SEED IMS"
                value={searchQuery}
                onChange={handleSearchChange}
                onBlur={handleSearchBlur}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
              />
              {showSearchResults && (
                <div className={styles.searchResults}>
                  {isSearching ? (
                    <div className={styles.searchResultItem} style={{ textAlign: "center", color: "#6c757d" }}>
                      Searching...
                    </div>
                  ) : searchResults.length === 0 ? (
                    <div className={styles.searchResultItem} style={{ textAlign: "center", color: "#6c757d" }}>
                      No results found
                    </div>
                  ) : (
                    searchResults.map((result) => (
                      <div
                        key={`${result.type}-${result.id}`}
                        className={styles.searchResultItem}
                        onClick={() => handleSearchResultClick(result)}
                        onMouseDown={(e) => e.preventDefault()} // Prevent blur before click
                      >
                        <div className={styles.searchResultType}>
                          {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                        </div>
                        <div className={styles.searchResultTitle}>{result.title}</div>
                        {result.subtitle && (
                          <div className={styles.searchResultSubtitle}>{result.subtitle}</div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </form>
            <div className={styles.headerIcons}>
              <button type="button" className={styles.iconButton} aria-label="Notifications">
                <NotificationsOutlinedIcon fontSize="small" />
              </button>
              <button type="button" className={styles.iconButton} aria-label="Help center">
                <HelpOutlineOutlinedIcon fontSize="small" />
              </button>
            </div>
            <div className={styles.userProfile}>
              <img
                className={styles.avatar}
                src="https://i.pravatar.cc/80?img=12"
                alt="Administrative user avatar"
              />
              <div className={styles.userInfo}>
                <span>{session.user.email}</span>
                <button
                  type="button"
                  className={styles.signOutButton}
                  onClick={() => {
                    signOut();
                    navigate("/auth/login");
                  }}
                  aria-label="Sign out"
                >
                  Sign out
                  <LogoutIcon
                    fontSize="inherit"
                    style={{ marginLeft: "6px", verticalAlign: "middle" }}
                  />
                </button>
              </div>
            </div>
          </div>
        </header>

        {isLoading ? (
          <div className={styles.card}>
            <p>Loading dashboard insights…</p>
          </div>
        ) : null}
        {error ? (
          <div className={styles.card}>
            <strong>Unable to load dashboard data.</strong>
            <span className={styles.trendNeutral}>{error}</span>
          </div>
        ) : null}

        <section className={styles.dashboardGrid}>
          {metrics.map((metric) => (
            <article key={metric.key} className={styles.card} aria-live="polite">
              <div className={styles.cardHeader}>
                <span className={styles.cardTitle}>{metric.title}</span>
              </div>
              <div className={styles.metricValue}>{metric.value}</div>
              <div className={metric.trendClass}>{metric.trend}</div>
            </article>
          ))}

          <article className={`${styles.card} ${styles.chartCard}`} aria-label="Cases vs Applications">
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Pipeline Outlook</span>
            </div>
            <div className={styles.chartContainer}>
              <Line
                data={lineChartData}
                options={{
                  maintainAspectRatio: false,
                  responsive: true,
                  plugins: {
                    legend: { position: "bottom", align: "start" }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: { precision: 0 }
                    }
                  }
                }}
                aria-label="Line chart showing case and application statuses"
              />
            </div>
          </article>

          <article className={styles.card} aria-label="Program Enrollments">
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Program Distribution</span>
            </div>
            <div className={styles.expenseLayout}>
              <div className={styles.chartContainer}>
                <Doughnut
                  data={doughnutData}
                  options={{
                    maintainAspectRatio: false,
                    cutout: "72%",
                    plugins: { legend: { display: false } }
                  }}
                  aria-label="Doughnut chart showing program enrollment share"
                />
              </div>
              <div className={styles.legend}>
                {doughnutData.labels.map((label, index) => (
                  <div className={styles.legendItem} key={label}>
                    <div className={styles.legendLabel}>
                      <span
                        className={styles.legendDot}
                        style={{ backgroundColor: legendColors[index % legendColors.length] }}
                      />
                      {label}
                    </div>
                    <span>{doughnutData.datasets[0].data[index]} households</span>
                  </div>
                ))}
              </div>
            </div>
          </article>

          {/* TODO: Implement Program Health metrics with real data
          <article className={`${styles.card} ${styles.financeScore}`}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Program Health</span>
            </div>
            <span>Disbursement Quality</span>
            <strong>Stable</strong>
            <div className={styles.financeValue}>92%</div>
            <div className={styles.progressBar} aria-hidden="true">
              <div className={styles.progressFill} style={{ width: "92%" }} />
            </div>
            <p className={styles.trendNeutral}>
              Based on on-time payments, reconciliation velocity, and audit log coverage.
            </p>
          </article>
          */}

          {/* TODO: Implement Funding Accounts with real bank integration
          <article className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Funding Accounts</span>
            </div>
            <span>Total Available Balance</span>
            <div className={styles.balanceTotal}>$1,377,000</div>
            <div className={styles.accountItem}>
              <div className={`${styles.accountIcon} ${styles.visa}`}>SEED</div>
              <div className={styles.accountDetails}>
                <span>Commercial Bank Operating</span>
                <span className={styles.accountBalance}>$532,000</span>
              </div>
            </div>
            <div className={styles.accountItem}>
              <div className={`${styles.accountIcon} ${styles.mastercard}`}>BRISP</div>
              <div className={styles.accountDetails}>
                <span>Cooperative Bank Trust</span>
                <span className={styles.accountBalance}>$415,000</span>
              </div>
            </div>
          </article>
          */}

          {/* TODO: Implement Recent Transactions with real transaction data
          <article className={`${styles.card} ${styles.chartCard}`}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Recent Transactions</span>
            </div>
            <div className={styles.table} role="region" aria-live="polite">
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">Transaction</th>
                    <th scope="col">Account</th>
                    <th scope="col">Date &amp; Time</th>
                    <th scope="col">Amount</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length ? (
                    transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{tx.name}</td>
                        <td>{tx.account}</td>
                        <td>{tx.timestamp}</td>
                        <td>${tx.amount.toLocaleString()}</td>
                        <td>
                          <span
                            className={`${styles.statusBadge} ${
                              tx.status === "completed"
                                ? styles.statusCompleted
                                : tx.status === "pending"
                                  ? styles.statusPending
                                  : styles.statusDeclined
                            }`}
                          >
                            {tx.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className={styles.emptyState}>
                        No transactions recorded yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </article>
          */}

          {/* TODO: Implement Strategic Initiatives with real project data
          <article className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Strategic Initiatives</span>
            </div>
            <div>
              {savingPlans.length ? (
                savingPlans.map((plan) => {
                  const percentage = Math.min(100, Math.round((plan.current / plan.goal) * 100));
                  return (
                    <div key={plan.id} className={styles.savingItem}>
                      <div className={styles.savingHeader}>
                        <span>{plan.name}</span>
                        <span>
                          ${plan.current.toLocaleString()} / ${plan.goal.toLocaleString()}
                        </span>
                      </div>
                      <div className={styles.progressBar} aria-hidden="true">
                        <div className={styles.progressFill} style={{ width: `${percentage}%` }} />
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className={styles.emptyState}>No initiatives configured yet.</p>
              )}
            </div>
          </article>
          */}

          <article className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.cardTitle}>Recent Activities</span>
            </div>
            <div>
              {activities.length ? (
                activities.map((activity) => (
                  <div key={activity.id} className={styles.activityItem}>
                    <span className={styles.activityIcon}>{activity.icon}</span>
                    <div className={styles.activityDetails}>
                      <span>{activity.text}</span>
                      <span className={styles.activityTime}>{activity.time}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className={styles.emptyState}>Activity feed will populate as actions occur.</p>
              )}
            </div>
          </article>
        </section>
      </main>
    </div>
  );
};
